import { useMutation, useQuery } from '@tanstack/react-query';
import type { BusinessFocus, Tenant } from '@cartiq/shared';
import { ApiError, apiGet, apiPost, bootstrapAuth } from './client';
import { queryKeys } from './queryKeys';
import { useAuthStore, type AuthUser } from '../stores/authStore';

export interface AuthResult {
  accessToken: string;
  user: AuthUser;
  tenant: Tenant;
}

export interface SignupInput {
  name: string;
  email: string;
  password: string;
  storeName: string;
  businessFocus: BusinessFocus;
}

export interface LoginInput {
  email: string;
  password: string;
}

export function useSignup() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: SignupInput) => apiPost<AuthResult>('/auth/signup', input),
    onSuccess: (data) => setSession(data),
  });
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: (input: LoginInput) => apiPost<AuthResult>('/auth/login', input),
    onSuccess: (data) => setSession(data),
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({
    mutationFn: () => apiPost<{ ok: true }>('/auth/logout'),
    onSettled: () => logout(),
  });
}

export interface MeResult {
  user: AuthUser;
  tenant: Tenant;
}

/** Bootstraps the session from an httpOnly refresh cookie on app load. */
export function useMe(enabled: boolean) {
  const setUser = useAuthStore((s) => s.setUser);
  const setTenant = useAuthStore((s) => s.setTenant);
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      // `/auth/refresh` mints a token AND returns the full session in one call,
      // so a cold-load restore is a single round-trip. If there's no session to
      // restore, bail. Only fall back to `/auth/me` in the rare case the store
      // came up with a token but no user/tenant (e.g. a token-only cross-tab
      // broadcast), so the page never renders without an identity.
      const token = await bootstrapAuth();
      if (!token) throw new ApiError('Not authenticated', 'UNAUTHENTICATED');
      const { user, tenant } = useAuthStore.getState();
      if (user && tenant) return { user, tenant };
      const data = await apiGet<MeResult>('/auth/me');
      setUser(data.user);
      setTenant(data.tenant);
      return data;
    },
    enabled,
    // Don't retry a genuinely-expired cookie (that just means "log in"), but do
    // retry once on a network/server blip so a transient hiccup doesn't bounce a
    // valid session to /login.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return false;
      return failureCount < 1;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface SessionRestore {
  /** A live access token is present — the user is authenticated. */
  token: string | null;
  /** The user was authenticated before; worth attempting a cookie restore. */
  hint: boolean;
  /** A restore attempt is in flight. */
  restoring: boolean;
  /** Restore finished and produced a usable session. */
  restored: boolean;
  /** Restore was attempted but failed (expired/absent cookie). */
  failed: boolean;
}

/**
 * Single source of truth for the cold-load session handshake shared by the
 * route guards. The access token lives only in memory, so every fresh tab
 * starts tokenless and rebuilds the session from the refresh cookie.
 */
export function useSessionRestore(): SessionRestore {
  const token = useAuthStore((s) => s.accessToken);
  const hint = useAuthStore((s) => s.isAuthedHint);
  const { isLoading, isError } = useMe(!token && hint);

  const restored = token ? true : !isError && !!useAuthStore.getState().accessToken;

  return {
    token,
    hint,
    restoring: !token && hint && isLoading,
    restored,
    failed: !token && hint && !isLoading && !restored,
  };
}
